import React, { useId } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TAX_CLASSIFICATIONS_FRONTEND } from '~/constants.ts';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantInfoContext } from '~/features/leadDetails/context/ApplicantInfoContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

export const TaxClass = ({ secondApplicant }: { secondApplicant?: boolean }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { applicantId } = useApplicantInfoContext();
    const { t } = useTranslation();
    const { control } = useFormContext();
    const labelId = useId();

    const options = Object.values(TAX_CLASSIFICATIONS_FRONTEND).map((value) => ({
        label: t(`TAX_CLASSIFICATION.${value}`),
        value,
    }));

    const isDisabled = !canExecuteApplicantCommand({
        applicant: secondApplicant ? 2 : 1,
        commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmployment,
        permissions,
    });

    return (
        <FormField<FieldValues, string | null> control={control} name="beschaeftigung.steuerklasse">
            {({ error, onChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start" id={labelId}>
                        {t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.TAX_CLASSIFICATIONS')}
                    </Label>
                    <DropdownMenu
                        aria-labelledby={labelId}
                        data-testid={`beschaeftigung.steuerklasse-${applicantId}`}
                        options={options}
                        value={value ?? TAX_CLASSIFICATIONS_FRONTEND.NONE}
                        onChange={(newValue) => onChange(newValue as string)}
                        placeholder=""
                        triggerClassName="w-full justify-between"
                        className="w-full"
                        disabled={isDisabled}
                        error={error}
                        scrollable
                    />
                </div>
            )}
        </FormField>
    );
};
